import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
// ❌ REMOVED unused import: import aiImage from '../assets/ai-multieye.png';
import SecurityComplianceSection from '../components/SecurityComplianceSection';
import ConsultationCTA from '../components/ConsultationCTA';
import {
  FaMoneyBillWave,
  FaCheckCircle,
  FaBolt,
  FaLock,
  FaCogs,
  FaUserClock,
} from 'react-icons/fa';
import 'animate.css';
import AOS from 'aos';
import 'aos/dist/aos.css';

const Why = () => {
  const [screen, setScreen] = useState('desktop');

  useEffect(() => {
    const detectScreen = () => {
      const width = window.innerWidth;
      if (width < 768) setScreen('mobile');
      else if (width < 1024) setScreen('tablet');
      else setScreen('desktop');
    };
    detectScreen();
    window.addEventListener('resize', detectScreen);
    return () => window.removeEventListener('resize', detectScreen);
  }, []);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const isMobile = screen === 'mobile';
  const isTablet = screen === 'tablet';

  const getFontSize = (mobile, tablet, desktop) =>
    isMobile ? mobile : isTablet ? tablet : desktop;

  const sectionPadding = isMobile ? '30px 20px' : isTablet ? '50px 40px' : '60px 80px';

  // Compliance Icon Component
  const ComplianceIcon = ({ number, title, subtitle, borderColor, textColor }) => (
    <div
      style={{
        width: isMobile ? '120px' : '140px',
        height: isMobile ? '120px' : '140px',
        border: `3px solid ${borderColor}`,
        borderRadius: '15px',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '15px',
        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
      }}
    >
      <div
        style={{
          fontSize: isMobile ? '32px' : '38px',
          fontWeight: 'bold',
          color: textColor,
          marginBottom: '8px',
        }}
      >
        {number}
      </div>
      {title && (
        <div
          style={{
            fontSize: isMobile ? '11px' : '13px',
            color: '#666',
            textAlign: 'center',
            lineHeight: '1.3',
          }}
        >
          {title}
          {subtitle && (
            <>
              <br />
              {subtitle}
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags - SEO OPTIMIZED */}
        <title>Why Choose MDeRCM? | Best RCM Company USA | MDeRCM</title>
        <meta 
          name="description" 
          content="Discover why MDeRCM is the top RCM company. 35-45% denial reduction, 30-50% cost savings, AI-powered billing. Trusted by 200+ practices." 
        />
        
        {/* Keywords */}
        <meta 
          name="keywords" 
          content="best revenue cycle management companies, healthcare revenue cycle management services, medical billing outsourcing services, revenue cycle management services USA, hospital revenue cycle management, outsourced revenue cycle management services, healthcare cost reduction strategies, how to improve revenue cycle management" 
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mdercm.com/why" />
        <meta property="og:title" content="Why Choose MDeRCM | Best Revenue Cycle Management Companies" />
        <meta property="og:description" content="Expert healthcare revenue cycle management services with AI-powered automation. Reduce costs by 30-50% with proven RCM solutions." />
        <meta property="og:image" content="https://mdercm.com/hero-bg.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://mdercm.com/why" />
        <meta property="twitter:title" content="Why Choose MDeRCM | Healthcare Revenue Cycle Management" />
        <meta property="twitter:description" content="Leading healthcare RCM services with AI automation and expert support." />

        {/* Canonical URL */}
        <link rel="canonical" href="https://mdercm.com/why" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Healthcare Revenue Cycle Management Services",
            "provider": {
              "@type": "Organization",
              "name": "MDeRCM"
            },
            "description": "AI-powered healthcare revenue cycle management services for hospitals and clinics across the USA",
            "areaServed": "United States",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "RCM Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Medical Billing Outsourcing Services"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <div style={{ fontFamily: 'Poppins, sans-serif', overflowX: 'hidden', boxSizing: 'border-box' }}>
        {/* TOP SECTION - SEO OPTIMIZED */}
        <section 
          style={{ width: '100%', backgroundColor: '#fff', padding: sectionPadding }} 
          data-aos="fade-up"
          aria-label="Why Choose MDeRCM for Revenue Cycle Management"
        >
          <div
            style={{
              maxWidth: '1140px',
              width: '100%',
              paddingLeft: '20px',
              paddingRight: '20px',
              boxSizing: 'border-box',
              margin: '0 auto',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '40px',
            }}
          >
            <div style={{ flex: 1, minWidth: '300px' }}>
              {/* H1 with Primary Keywords */}
              <h1
                style={{
                  fontSize: getFontSize('24px', '28px', '36px'),
                  fontWeight: '700',
                  color: '#0b163f',
                  lineHeight: '1.4',
                  marginBottom: '25px',
                }}
              >
                Why Choose MDeRCM for Healthcare Revenue Cycle Management Services?
              </h1>

              {/* Supporting text with keywords */}
              <div
                style={{
                  backgroundColor: '#001f3f',
                  color: '#ffffff',
                  padding: '25px 30px',
                  borderRadius: '10px',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                  marginBottom: '30px',
                }}
              >
                <p style={{ fontSize: '16px', lineHeight: '1.8', marginBottom: '15px' }}>
                  MDeRCM is among the <strong style={{ color: '#00cec9' }}>best revenue cycle management companies</strong> in the USA, offering comprehensive <strong style={{ color: '#00cec9' }}>healthcare revenue cycle management services</strong> powered by advanced AI technology.
                </p>
                <p style={{ fontSize: '15px', lineHeight: '1.7' }}>
                  Our <strong style={{ color: '#00cec9' }}>medical billing outsourcing services</strong> combine expert knowledge with intelligent automation to deliver proven <strong style={{ color: '#00cec9' }}>healthcare cost reduction strategies</strong> that reduce operational costs by 30-50%.
                </p>
              </div>

              <ul style={{ fontSize: '15px', lineHeight: '2', color: '#333', paddingLeft: '20px' }}>
                <li><strong>AI-Powered Automation:</strong> Streamline revenue cycle operations with intelligent technology</li>
                <li><strong>Expert RCM Consulting:</strong> Professional guidance on how to improve revenue cycle management</li>
                <li><strong>HIPAA Compliant:</strong> Secure, compliant outsourced revenue cycle management services</li>
                <li><strong>Proven Results:</strong> Join 500+ healthcare providers reducing costs by 30-50%</li>
                <li><strong>24/7 Support:</strong> Dedicated US-based team for hospital revenue cycle management</li>
              </ul>
            </div>

            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                minWidth: '300px',
              }}
            >
              <h2 style={{
                fontSize: getFontSize('22px', '26px', '32px'),
                fontWeight: '700',
                color: '#0b163f',
                marginBottom: '15px',
              }}>
                Compliance Standards We Meet
              </h2>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '15px',
                  justifyContent: 'flex-start',
                }}
              >
               
        
                <ComplianceIcon number="ISO" title="27001" subtitle="9001" borderColor="#e17055" textColor="#e17055" />
                <ComplianceIcon number="HIPPA" borderColor="#d63031" textColor="#d63031" />
              </div>

              <p style={{
                fontSize: '14px',
                color: '#666',
                lineHeight: '1.7',
                marginTop: '15px',
                fontStyle: 'italic',
              }}>
                As one of the leading <strong>revenue cycle management services companies</strong>, we maintain the highest security and compliance standards for all our <strong>medical billing and coding outsourcing</strong> services.
              </p>
            </div>
          </div>
        </section>

        {/* BENEFITS SECTION - SEO OPTIMIZED */}
        <section
          style={{
            width: '100%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            padding: sectionPadding,
          }}
          data-aos="fade-up"
          aria-label="Revenue Cycle Management Benefits"
        >
          <div
            style={{
              maxWidth: '1140px',
              margin: '0 auto',
              width: '100%',
              paddingLeft: '20px',
              paddingRight: '20px',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <h2
              style={{
                fontSize: getFontSize('26px', '32px', '40px'),
                fontWeight: '700',
                marginBottom: '25px',
                textAlign: 'center',
              }}
            >
              Benefits of AI-Powered Revenue Cycle Management Services
            </h2>

            {/* Supporting paragraph with keywords */}
            <p style={{
              fontSize: '16px',
              lineHeight: '1.8',
              color: '#fff',
              maxWidth: '900px',
              textAlign: 'center',
              marginBottom: '40px',
              opacity: 0.95,
            }}>
              Discover why leading <strong>revenue cycle management services companies</strong> are choosing AI automation. Our <strong>medical billing outsourcing companies</strong> deliver measurable results through intelligent technology and expert support.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                gap: '30px',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              {[
                {
                  icon: <FaMoneyBillWave size={50} color="#00b894" />,
                  text: 'Minimizes costs by 30-50% through automation of repetitive tasks and streamlined resource allocation—proven healthcare cost reduction strategies',
                },
                {
                  icon: <FaCheckCircle size={50} color="#0984e3" />,
                  text: 'Boosts workforce efficiency by eliminating manual bottlenecks, allowing your team to focus on high-value patient care activities',
                },
                {
                  icon: <FaBolt size={50} color="#fdcb6e" />,
                  text: 'Enhances operational speed and accuracy by 40%+, enabling scalable and agile business processes across all RCM operations',
                },
                {
                  icon: <FaLock size={50} color="#e17055" />,
                  text: 'Ensures robust HIPAA-compliant data privacy and regulatory compliance with PHI, PII, and PCI standards for complete security',
                },
                {
                  icon: <FaCogs size={50} color="#6c5ce7" />,
                  text: 'Improves workflow consistency, accelerates service delivery, and reduces downtime risks through intelligent automation',
                },
                {
                  icon: <FaUserClock size={50} color="#d63031" />,
                  text: 'Cuts down manual labor and operational expenditure by 40-60% through intelligent automation and expert RCM consulting',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  data-aos="zoom-in"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    maxWidth: '320px',
                    backgroundColor: '#f9f9f9',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                  }}
                >
                  <div style={{ marginBottom: '15px', flexShrink: 0 }}>{item.icon}</div>
                  <p
                    style={{
                      fontSize: '15px',
                      color: '#0b163f',
                      fontWeight: '500',
                      lineHeight: '1.5',
                      textAlign: 'center',
                    }}
                  >
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DATA AND SECURITY SECTION - SEO OPTIMIZED */}
        <section
          style={{
            width: '100%',
            background: 'linear-gradient(to right, #0f2027, #203a43, #2c5364)',
            color: '#ffffff',
            padding: sectionPadding,
          }}
          data-aos="fade-right"
          aria-label="HIPAA-Compliant Data Security"
        >
          <div
            style={{
              maxWidth: '1140px',
              width: '100%',
              paddingLeft: '20px',
              paddingRight: '20px',
              boxSizing: 'border-box',
              margin: '0 auto',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap',
              gap: '40px',
              alignItems: 'center',
            }}
          >
            <div style={{ flex: 1 }} data-aos="fade-up">
              {/* H2 with Keywords */}
              <h2
                style={{
                  fontSize: getFontSize('24px', '30px', '38px'),
                  fontWeight: '700',
                  marginBottom: '20px',
                }}
              >
                HIPAA-Compliant Data Security for Healthcare Revenue Cycle Management
              </h2>
              <p style={{ fontWeight: '600', marginBottom: '20px', fontSize: '17px' }}>
                At MDeRCM, safeguarding patient information is our top priority. As one of the <strong style={{ color: '#00cec9' }}>best revenue cycle management companies</strong>, we offer AI-driven solutions designed to streamline operations while ensuring complete HIPAA compliance and data security.
              </p>
              <p style={{ marginBottom: '15px', lineHeight: '1.7' }}>
                Our platform integrates automatic safeguards for sensitive data types like PHI, PII, and PCI, making it easy for healthcare organizations to stay compliant with the latest regulations through our <strong>outsourced revenue cycle management services</strong>.
              </p>
              <p style={{ marginBottom: '15px', lineHeight: '1.7' }}>
                With robust encryption protocols and secure authentication methods, MDeRCM's <strong>medical billing outsourcing services</strong> ensure that your data is protected both during transmission and when stored. Our dedicated security team continually works to fortify system security.
              </p>
              <p style={{ marginBottom: '15px', lineHeight: '1.7' }}>
                By partnering with our <strong>revenue cycle management services USA</strong> team, you're not just improving operational efficiency—you're safeguarding your patients' most sensitive data while implementing proven <strong>healthcare cost reduction strategies</strong>.
              </p>
            </div>

            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <FaLock
                size={isMobile ? 120 : 160}
                color="#00cec9"
                style={{ filter: 'drop-shadow(2px 4px 6px rgba(0,0,0,0.3))' }}
                data-aos="zoom-in"
              />
            </div>
          </div>
        </section>

        {/* Keywords Integration Section */}
        <section style={{
          width: '100%',
          backgroundColor: '#f8f9fa',
          padding: '50px 5%',
          textAlign: 'center',
        }}>
          <div style={{
            maxWidth: '1000px',
            margin: '0 auto',
          }}>
            <p style={{
              fontSize: '16px',
              lineHeight: '1.8',
              color: '#666',
              fontStyle: 'italic',
            }}>
              Looking for expert guidance on <strong>how to improve revenue cycle management</strong>? Our <strong>revenue cycle management consulting services</strong> help hospitals and clinics across the USA optimize their operations. As a trusted provider among <strong>revenue cycle management services companies</strong>, we specialize in <strong>hospital revenue cycle management</strong>, <strong>medical billing and coding outsourcing</strong>, and comprehensive <strong>healthcare cost reduction consulting</strong>.
            </p>
          </div>
        </section>

        <SecurityComplianceSection />
        <ConsultationCTA />
      </div>
    </>
  );
};

export default Why;